

import { CotacaoEcommerce } from "@/types/Cotacao";
import {
  BuscarCepResponse,
  CadastrarClienteContratoPayload,
  CadastrarClienteContratoResponse,
  CreateQuote,
  CreateQuoteResponse,
  Cupom,
  CupomError,
  Installment,
  MotivoCompra,
  MunicipioJson,
} from "@/types/FlowQuote";
import { VeiculoJson, VeiculoPlacaResponse } from "@/types/Veiculo";
import api from "./api";
import apiHmlg from "./apiHmlg";

const API_TARGET = import.meta.env.VITE_API_TARGET || "prod";

const ApiAmbient = API_TARGET.toLowerCase() === "hmlg" ? apiHmlg : api;

export const jsonMunicipios = async (): Promise<MunicipioJson[]> => {
  const response = await api.get<MunicipioJson[]>(
    "/api_codehouse_municipio_json.php",
  );
  return response.data;
};

export const jsonVeiculos = async (): Promise<VeiculoJson[]> => {
  const response = await api.get<VeiculoJson[]>(
    "/api_codehouse_veiculo_json.php",
  );
  return response.data;
};

export const jsonMotivo = async (): Promise<MotivoCompra[]> => {
  const response = await api.get<MotivoCompra[]>(
    "/api_codehouse_motivos_cotacao_json.php",
  );
  return response.data;
};

export const consultarPlaca = async (
  cve_placa: string,
  cve_codigo: number,
): Promise<VeiculoPlacaResponse> => {
  const response = await ApiAmbient.post<VeiculoPlacaResponse>(
    "/ecom/api_placa.php",
    {
      cve_placa,
      cve_codigo,
    },
  );
  return response.data;
};

export const cotacaoId = async (
  cve_codigo: string,
  cve_token: string,
): Promise<CotacaoEcommerce[]> => {
  const params: { cve_codigo: string; cve_token: string } = {
    cve_codigo,
    cve_token,
  };

  const response = await ApiAmbient.get<CotacaoEcommerce[]>(
    "/ecom/api_cotacao_ecommerce_json.php",
    { params },
  );
  return response.data;
};

export const buscarCep = async (cep: string): Promise<BuscarCepResponse> => {
  const response = await ApiAmbient.post<BuscarCepResponse>(
    "/ecom/api_cep.php",
    { cep },
  );
  return response.data;
};

export const fazerCotacao = async (
  data: CreateQuote,
): Promise<CreateQuoteResponse> => {
  const response = await ApiAmbient.post<CreateQuoteResponse>(
    "/ecom/api_grava_lead_ecommerce_banco.php",
    data,
  );
  return response.data;
};

export const updateCotacaoField = async (
  cve_codigo: string,
  field: string,
  value: string,
): Promise<void> => {
  try {
    await ApiAmbient.post("/ecom/api_alterar_cotacao_ecommerce_banco.php", {
      cve_codigo,
      [field]: value,
    });
  } catch (error) {
    console.error(`Erro ao atualizar o campo ${field}:`, error);
    throw error;
  }
};

export const updateCotacaoFields = async (
  cve_codigo: string,
  fields: Record<string, string>,
): Promise<void> => {
  try {
    await ApiAmbient.post("/ecom/api_alterar_cotacao_ecommerce_banco.php", {
      cve_codigo,
      ...fields,
    });
  } catch (error) {
    console.error("Erro ao atualizar campos da cotação:", error);
  }
};

export const calcularColetaEntrega = async (
  cve_codigo: string,
  bop_codigo: string,
  cep: string,
  tipo: "coleta" | "entrega",
): Promise<Record<string, unknown>> => {
  const response = await ApiAmbient.post<Record<string, unknown>>(
    "/ecom/api_calcular_coleta_entrega.php",
    {
      cve_codigo,
      bop_codigo,
      cep,
      tipo,
    },
  );
  return response.data;
};

export const buscarCupons = async (): Promise<Cupom[]> => {
  try {
    const response = await ApiAmbient.post<Cupom[] | CupomError>(
      "/bot/api_bot_cupons.php",
      {
        cup_aplicacao: "GERAL",
      },
    );

    if ("erro" in response.data) {
      return [];
    }
    return response.data as Cupom[];
  } catch (error) {
    console.error("Erro ao buscar cupons:", error);
    return [];
  }
};

export const aplicarCupom = async (
  cup_codigo: string,
  ctc_codigo: string,
): Promise<"Sucesso" | "Erro"> => {
  try {
    const response = await ApiAmbient.post("/bot/api_bot_aplicar_cupom.php", {
      cup_codigo,
      ctc_codigo,
    });
    return response.data === "Sucesso" ? "Sucesso" : "Erro";
  } catch (error) {
    console.error("Erro ao aplicar cupom:", error);
    return "Erro";
  }
};

export const cadastrarClienteContrato = async (
  payload: CadastrarClienteContratoPayload,
): Promise<CadastrarClienteContratoResponse> => {
  const response = await ApiAmbient.post(
    "/ecom/api_cadastrar_cliente_contrato.php",
    payload,
  );

  return response.data;
};

export const getSalvarContratoEcommerceUrl = (
  cve_codigo: string,
  ctc_codigo: string,
  cli_codigo: string,
): string => {
  const baseUrl =
    ApiAmbient.defaults.baseURL ||
    (API_TARGET.toLowerCase() === "hmlg"
      ? "https://h.autolog.nacionaltransportes.com"
      : "https://api.nacionaltransportes.com");
  const params = new URLSearchParams({ cve_codigo, ctc_codigo, cli_codigo });
  return `${baseUrl}/api_salva_contrato_ecommerce.php?${params.toString()}`;
};

export const leaveCotacaoBeacon = (cve_codigo: string, cve_token: string) => {
  const baseUrl =
    ApiAmbient.defaults.baseURL ||
    (API_TARGET.toLowerCase() === "hmlg"
      ? "https://h.autolog.nacionaltransportes.com"
      : "https://api.nacionaltransportes.com");

  const url = `${baseUrl}/ecom/api_mensagem_abandono.php`;

  const payload = new FormData();
  payload.append("cve_codigo", cve_codigo);
  payload.append("cve_token", cve_token);

  navigator.sendBeacon(url, payload);
};

export const getInstallments = async (
  valor: number,
): Promise<Installment[]> => {
  const response = await ApiAmbient.post<Installment[]>(
    "/ecom/api_parcelas_cartao.php",
    {
      valor,
    },
  );
  return response.data;
};

export const registrarContratoAssinado = async (
  ctc_codigo: string,
): Promise<void> => {
  let ip = "";
  try {
    const ipResponse = await fetch("https://api.ipify.org?format=json");
    const data = await ipResponse.json();
    ip = data.ip ?? "";
  } catch {
    // IP fetch blocked or unavailable — proceed without it
  }
  const params = new URLSearchParams({ ctc_codigo, ip });
  ApiAmbient.get(
    `/api_contrato_assinado_ecommerce.php?${params.toString()}`,
  ).catch(() => {});
};
